import React, { useState } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import configData from "../config.json";
import { GetSession, GetToken } from '../components/Session';
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { FormInput, FormSwitch, FormTextArea, FormNumericTextBox, FormDropDownList, FormAddress} from "../components/formComponents";
import { Button } from "@progress/kendo-react-buttons";
import { GetPermissions } from '../components/Options';
import { processMultiSelectTreeData } from "../components/multiselecttree-data-operations";
import { MultiSelectTreeEditor } from "../components/MultiSelectTreeEditor";
import { Map, MapLayers, MapTileLayer, MapMarkerLayer } from '@progress/kendo-react-map';
import { Dialog } from "@progress/kendo-react-dialogs";
import ExpansionPanel from "../components/ExpansionPanel"; // import the above component

const EditCustomers = () => {
    const { id } = useParams(); // editcustomers/type/id url is used to edit existing customer, otherwise id is undefined
    const { type } = useParams(); // type S = Supplier, C = customer
    const saveName = id === undefined ? "Create" : "Update";
    const [formkey, setFormkey] = React.useState(0); // seems to be needed to push values to form after loading
    const [states, setStates] = React.useState([ ]);
    const [paymentMethods, setPaymentMethods] = React.useState([]);
    const [isMonthly, setIsMonthly] = React.useState(false);
    const [syncIntervals, setSyncIntervals] = React.useState([ ]);
    const navigate = useNavigate();
    const [permissions, setPermissions] = React.useState(null);
    const [centre, setCentre] = React.useState([-33.8, 151]);
    const tileUrl = e => `https://${e.subdomain}.tile.openstreetmap.org/${e.zoom}/${e.x}/${e.y}.png`;
    const [markers, setMarkers] = React.useState([]);
    const [zoom, setZoom] = React.useState(12);
    const [showMap, setShowMap] = React.useState(false);
    const allOptions = React.useRef(null);
    const [submitDisabled, setSubmitDisabled] = React.useState(false);
    const [expandedPanels, setExpandedPanels] = useState({
        customerDetails: true,
        addressDetails: false,
        deliveryDetails: false,
        siteDetails: true,
    });

    const togglePanel = (panel) => {
        setExpandedPanels(prev => ({ ...prev, [panel]: !prev[panel] }));
    };

    // default layout data for page
    const [layout, setLayout] = React.useState([
        { "field": "customerDetails", "label": "Customer", "defaultValue": "", "isVisible": true, "type": "heading"},
        { "field": "name", "label": "Name", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "description", "label": "Notes", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "accountNumber", "label": "Account", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "paymentMethod", "label": "Payment Method", "defaultValue": "ACCOUNT", "isVisible": true, "type": "text"},
        { "field": "siteCreated", "label": "SiteCreated", "defaultValue": "", "isVisible": false, "type": "boolean"},
        { "field": "address1", "label": "Address Line 1", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "address2", "label": "Address Line 2", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "suburb", "label": "Suburb", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "state", "label": "State", "defaultValue": "NSW", "isVisible": true, "type": "text"},
        { "field": "postcode", "label": "Postcode", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "country", "label": "Country", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "phone", "label": "Phone", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "fax", "label": "Fax", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "mobile", "label": "Mobile", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "contact", "label": "Contact", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "comment", "label": "Comment", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "email", "label": "Email", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "abn", "label": "ABN", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "deliveryContact", "label": "Delivery Contact", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "deliveryAddress1", "label": "Delivery Address 1", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "deliveryAddress2", "label": "Delivery Address 2", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "deliverySuburb", "label": "Delivery Suburb", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "deliveryState", "label": "Delivery State", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "deliveryPostcode", "label": "Delivery Postcode", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "deliveryZone", "label": "Delivery Zone", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "deliveryPhone", "label": "Delivery Phone", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "tagId", "label": "Tag ID", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "customerType", "label": "CustomerType", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "fixedCharge", "label": "FixedCharge", "defaultValue": 0, "isVisible": true, "type": "numeric"},
        { "field": "discount", "label": "Discount (% off)", "defaultValue": 0, "isVisible": true, "type": "numeric"},
        { "field": "noGST", "label": "No GST", "defaultValue": false, "isVisible": true, "type": "boolean"},
        { "field": "hidePriceOnTicket", "label": "Hide Price On Ticket", "defaultValue": false, "isVisible": true, "type": "boolean"},
        { "field": "ticketCopies", "label": "Ticket Copies", "defaultValue": 1, "isVisible": true, "type": "numeric"},
        { "field": "fixedCartage", "label": "Fixed Delivery Cartage", "defaultValue": 0, "isVisible": true, "type": "numeric"},
        { "field": "isOrderNoRequired", "label": "Order Number Required", "defaultValue": false, "isVisible": true, "type": "boolean"},
        { "field": "isAlwaysReqOrderNoPerTxn", "label": "Unique Order Number Required For Each Transaction", "defaultValue": false, "isVisible": true, "type": "boolean"},
        { "field": "docketInitials", "label": "DocketInitials", "defaultValue": "", "isVisible": true, "type": "text"},
        { "field": "active", "label": "Active", "defaultValue": "TRUE", "isVisible": true, "type": "boolean"},
        { "field": "addressDetails", "label": "Address and contact details", "defaultValue": "", "isVisible": true, "type": "heading"},
        { "field": "deliveryDetails", "label": "Delivery address and contact details", "defaultValue": "", "isVisible": true, "type": "heading"},
        { "field": "siteDetails", "label": "Site details", "defaultValue": "", "isVisible": true, "type": "heading"},
        { "field": "myobSyncInterval", "label": "MYOB Sync Intervals", "defaultValue": "Daily", "isVisible": true, "type": "text"},
        { "field": "monthlySyncDay", "label": "Monthly Sync Day", "defaultValue": 1, "isVisible": true, "type": "numeric"},
        { "field": "customerSites", "label": "Sites assigned", "defaultValue": 1, "isVisible": true, "type": "list"},
        { "field": "customerTrucks", "label": "Trucks", "defaultValue": 1, "isVisible": true, "type": "list"},
        { "field": "smsDocket", "label": "Send Docket via SMS", "defaultValue": false, "isVisible": true, "type": "boolean" },
        { "field": "emailDocket", "label": "Send Docket via Email", "defaultValue": false, "isVisible": true, "type": "boolean" }
    ]);
    if (localStorage.getItem("userid") === null) navigate("/logout");

    // Gets field from layout based on field name
    const GetField = (f, def = "") => {
        let fld = layout.find(obj => { return obj.field === f });
        if (fld === undefined) return { "field": f, "label": def, "defaultValue": "", "isVisible": true, "type": "text" }
        return fld;
    }

    // State data for form. Values match form value on submit
    const [values, setValues] = React.useState({
        name: GetField("name")["defaultValue"],
        accountNumber: GetField("accountNumber")["defaultValue"],
        description: GetField("description")["defaultValue"],
        paymentMethod: GetField("paymentMethod")["defaultValue"],
        address1: GetField("address1")["defaultValue"],
        address2: GetField("address2")["defaultValue"],
        suburb: GetField("suburb")["defaultValue"],
        state: GetField("state")["defaultValue"],
        postcode: GetField("postcode")["defaultValue"],
        country: GetField("country")["defaultValue"],
        phone: GetField("phone")["defaultValue"],
        fax: GetField("fax")["defaultValue"],
        mobile: GetField("mobile")["defaultValue"],
        contact: GetField("contact")["defaultValue"],
        comment: GetField("comment")["defaultValue"],
        email: GetField("email")["defaultValue"],
        abn: GetField("abn")["defaultValue"],
        delivery: GetField("delivery")["defaultValue"],
        deliveryAddress1: GetField("deliveryAddress1")["defaultValue"],
        deliveryAddress2: GetField("deliveryAddress2")["defaultValue"],
        deliverySuburb: GetField("deliverySuburb")["defaultValue"],
        deliveryState: GetField("deliveryState")["defaultValue"],
        deliveryPostcode: GetField("deliveryPostcode")["defaultValue"],
        deliveryZone: GetField("deliveryZone")["defaultValue"],
        deliveryPhone: GetField("deliveryPhone")["defaultValue"],
        tagId: GetField("tagId")["defaultValue"],
        customerType: GetField("customerType")["defaultValue"],
        fixedCharge: GetField("fixedCharge")["defaultValue"],
        discount: GetField("discount")["defaultValue"],
        noGST: GetField("noGST")["defaultValue"],
        hidePriceOnTicket: GetField("hidePriceOnTicket")["defaultValue"],
        ticketCopies: GetField("ticketCopies")["defaultValue"],
        fixedCartage: GetField("fixedCartage")["defaultValue"],
        isOrderNoRequired: GetField("isOrderNoRequired")["defaultValue"],
        isAlwaysReqOrderNoPerTxn: GetField("isAlwaysReqOrderNoPerTxn")["defaultValue"],
        docketInitials: GetField("docketInitials")["defaultValue"],
        addressDetails: GetField("addressDetails")["defaultValue"],
        deliveryDetails: GetField("deliveryDetails")["defaultValue"],
        siteDetails: GetField("siteDetails")["defaultValue"],
        myobSyncInterval: GetField("myobSyncInterval")["defaultValue"],
        monthlySyncDay: GetField("monthlySyncDay")["defaultValue"],
        active: String(GetField("active")["defaultValue"]).toLowerCase() === "true",
        customerSites: [],
        customerTrucks: [],
        smsDocket: GetField("smsDocket")["defaultValue"],
        emailDocket: GetField("emailDocket")["defaultValue"],
      });

    // When page first loads:
    // - populate drop downs from apiv7/options
    // - if in edit mode, set initial values for all fields for apiv7/vehicleconfigurations
    React.useEffect(() =>
    {
        GetPermissions(["ViewCustomer", "NewCustomer", "EditCustomer"], setPermissions)
        var url = configData.SERVER_URL + "apiv7/userinterfaces?$filter=viewName eq 'EditCustomers'";
        const init = {
          method: 'GET',
          accept: 'application/json',
          headers: {"Authorization": "Bearer " + GetToken()}
        }
        fetch(url, init).then(response => response.json()).then(d => { 
            if (GetSession().IsMobile){
                let data = [];
                for(let i = 0; i < d.length; i++){
                    data.push({field: d[i].field, label: d[i].label, type:d[i].type, format: d[i].format, isVisible: d[i].isVisible && d[i].isVisibleOnMobile});
                }
                setLayout(data);
            } else {
                setLayout(d); 
            }
        });
        url = configData.SERVER_URL + "apiv7/options";
        fetch(url, init).then(response => response.json()).then(opt => {           
            allOptions.current = opt;
            setStates(opt.states);
            let allSites = GetList(opt.sites);
            let defSites = ConfigureSites(opt.sites, opt.inactiveSites, allSites);
            ConfigureTrucks(opt.trucks, opt.inactiveTrucks, "");
            setPaymentMethods(opt.paymentMethods);
            setSyncIntervals(opt.syncIntervals);
            if (id !== undefined){
                url = `${configData.SERVER_URL}apiv7/customers/${id}`;
                fetch(url, init).then(response => response.json()).then(d => {
                    let sites = ConfigureSites(opt.sites, opt.inactiveSites, d.siteList);                    
                    let trucks = ConfigureTrucks(opt.trucks, opt.inactiveTrucks, d.truckList);
                    setIsMonthly(d.myobSyncInterval === "Monthly");
                    setValues({
                        name: d.name === null ? "" : d.name,
                        accountNumber: d.accountNumber === null ? "" : d.accountNumber,
                        description: d.description === null ? "" : d.description,
                        paymentMethod: d.paymentMethod === null ? "" : d.paymentMethod,
                        address1: d.address1 === null ? "" : d.address1,
                        address2: d.address2 === null ? "" : d.address2,
                        suburb: d.suburb === null ? "" : d.suburb,
                        state: d.state === null ? "" : d.state,
                        postcode: d.postcode === null ? "" : d.postcode,
                        country: d.country === null ? "" : d.country,
                        customerSites: sites,
                        customerTrucks: trucks,
                        phone: d.phone === null ? "" : d.phone,
                        fax: d.fax === null ? "" : d.fax,
                        mobile: d.mobile === null ? "" : d.mobile,
                        contact: d.contact === null ? "" : d.contact,
                        comment: d.comment === null ? "" : d.comment,
                        email: d.email === null ? "" : d.email,
                        abn: d.abn === null ? "" : d.abn,
                        delivery: d.delivery === null ? "" : d.delivery,
                        deliveryAddress1: d.deliveryAddress1 === null ? "" : d.deliveryAddress1,
                        deliveryAddress2: d.deliveryAddress2 === null ? "" : d.deliveryAddress2,
                        deliverySuburb: d.deliverySuburb === null ? "" : d.deliverySuburb,
                        deliveryState: d.deliveryState === null ? "" : d.deliveryState,
                        deliveryPostcode: d.deliveryPostcode === null ? "" : d.deliveryPostcode,
                        deliveryZone: d.deliveryZone === null ? "" : d.deliveryZone,
                        deliveryPhone: d.deliveryPhone === null ? "" : d.deliveryPhone,
                        tagId: d.tagId === null ? "" : d.tagId,
                        customerType: d.customerType === null ? "" : d.customerType,
                        fixedCharge: d.fixedCharge,
                        discount: d.discount/100,
                        noGST: d.noGST,
                        hidePriceOnTicket: d.hidePriceOnTicket,
                        ticketCopies: d.ticketCopies,
                        fixedCartage: d.fixedCartage,
                        isOrderNoRequired: d.isOrderNoRequired,
                        isAlwaysReqOrderNoPerTxn: d.isAlwaysReqOrderNoPerTxn,
                        docketInitials: d.docketInitials === null ? "" : d.docketInitials,
                        addressDetails: d.addressDetails === null ? "" : d.addressDetails,
                        deliveryDetails: d.deliveryDetails === null ? "" : d.deliveryDetails,
                        siteDetails: d.siteDetails === null ? "" : d.siteDetails,
                        myobSyncInterval: d.myobSyncInterval === null ? "" : d.myobSyncInterval,
                        monthlySyncDay: d.monthlySyncDay,
                        active: d.isActive,
                        smsDocket: d.smsDocket,
                        emailDocket: d.emailDocket
                    });
                    setFormkey(formkey + 1);
                })
            } else {
                var v = {...values};
                v.customerSites = defSites;
                v.customerTrucks = [];
                setValues(v);                
                setFormkey(formkey + 1);
            }
        });
    },[]);

    // Returns value to be sent to web api:
    // If field is visible just return f
    // If field is hidden, return defaultValue from layout data
    const GetDefaultData = (f, name) => {
        let fld = GetField(name);
        if (fld === undefined || fld === null) return f;
        if (fld["isVisible"]) return f;
        let res = "";
        switch(fld["type"]){
            case "text": res = fld["defaultValue"]; break;
            case "numeric": res = parseFloat(fld["defaultValue"]); break;
            case "boolean": res = fld["defaultValue"] === "true"; break;
            default: res = fld["defaultValue"]; break;
        }
        return res;
    }

    const [sitesData, setSitesData] = React.useState([{id:0, text: "Active", items: []}]);
    const dataItemKey = "id";
    const checkField = "checkField";
    const checkIndeterminateField = "checkIndeterminateField";
    const subItemsField = "items";
    const expandField = "expanded";
    const fields = { dataItemKey, checkField, checkIndeterminateField, expandField, subItemsField };
    const [sitesValue, setSitesValue] = React.useState([]);
    const [siteExpanded, setSiteExpanded] = React.useState([]);
    const sitesTreeData = React.useMemo(() => processMultiSelectTreeData(sitesData, { expanded: siteExpanded, value: sitesValue, ...fields }), [siteExpanded, sitesValue]);
    const ConfigureSites = (act, dis, siteList) => {
        if (siteList === null || siteList === undefined) return [];
        let slsp = siteList.split(',');
        let sl = [];
        let selected = [];
        for (let i = 0; i < act.length; i++){
            let entry = { text: act[i].name, id: act[i].id };
            sl.push(entry);
            let found = slsp.find(obj => { return obj === String(act[i].id) });
            if (found !== undefined) selected.push(entry);
        }
        let active = {id: 0, text: 'Active', items: sl };
        if (dis !== undefined && dis.length > 0){
            sl = [];
            for (let i = 0; i < dis.length; i++){
                let entry = { text: dis[i].name, id: dis[i].id };
                sl.push(entry);       
                let found = slsp.find(obj => { return obj === String(dis[i].id) });
                if (found !== undefined) selected.push(entry);
            }
            let inactive = {id: 999999, text: 'Inactive', items: sl };
            setSitesData([active, inactive]);
        }
        else {
            setSitesData([active]);
        }
        setSitesValue([...selected]);       
        return [...selected];
    }

    const [trucksData, setTrucksData] = React.useState([{id:0, text: "Active", items: []}]);
    const [trucksValue, setTrucksValue] = React.useState([]);
    const [truckExpanded, setTruckExpanded] = React.useState([]);
    const trucksTreeData = React.useMemo(() => processMultiSelectTreeData(trucksData, { expanded: truckExpanded, value: trucksValue, ...fields }), [truckExpanded, trucksValue]);
    const ConfigureTrucks = (act, dis, siteList) => {
        if (siteList === null || siteList === undefined) return [];
        let slsp = siteList.split(',');
        let sl = [];
        let selected = [];
        for (let i = 0; i < act.length; i++){
            let entry = { text: act[i].name, id: act[i].id };
            sl.push(entry);
            let found = slsp.find(obj => { return obj === String(act[i].id) });
            if (found !== undefined) selected.push(entry);
        }
        let active = {id: 0, text: 'Active', items: sl };
        if (dis !== undefined && dis.length > 0){
            sl = [];
            for (let i = 0; i < dis.length; i++){
                let entry = { text: dis[i].name, id: dis[i].id };
                sl.push(entry);       
                let found = slsp.find(obj => { return obj === String(dis[i].id) });
                if (found !== undefined) selected.push(entry);
            }
            let inactive = {id: 999999, text: 'Inactive', items: sl };
            setTrucksData([active, inactive]);
        }
        else {
            setTrucksData([active]);
        }
        setTrucksValue([...selected]);       
        return [...selected];
    }

    const GetList = (obj) => {
        if (obj === undefined || obj === null || obj.length === 0) return "";
        var l = "";
        for (let i = 0; i < obj.length; i++){
            let id = obj[i].id;
            if (id !== 0 && id !== 999990){
                l += id;
                if (i < obj.length - 1) l += ",";
            }
        }
        return l;
    }

    const isNullOrEmpty = (x) => { return (x === undefined || x === null || x === ""); }
    
    const Validate = (dataItem) => {
        let st = "";
        if (isNullOrEmpty(dataItem.name)) {
            st += "Name\r\n";
        } else {
            let t = undefined;
            if (type === "C")
                t = allOptions.current.customers.find(obj => obj.name === dataItem.name && obj.id !== id);
            else
                t = allOptions.current.suppliers.find(obj => obj.name === dataItem.name && obj.id !== id);
            if (t !== undefined) {
                alert(dataItem.name + " already exists.\r\n");
                return false;
            }
        }
        if (st !== "") {
            setExpandedPanels({ customerDetails: true, addressDetails: false, deliveryDetails: false, siteDetails: false });
            alert("Please enter the following: \r\n" + st);
            return false;
        }   
        return true;
    }

    // Handle submit button. For create POST the data, for edit PUT the data, then redirect to offenses index page.
    const handleSubmit = () => {
        var dataItem = { ...values };
        if (!Validate(dataItem)) return;  
        var pth = id === undefined ? "" : "/" + id;
        var siteList = GetList(dataItem.customerSites);
        var truckList = GetList(dataItem.customerTrucks);
        setSubmitDisabled(true);
        var url = `${configData.SERVER_URL}apiv7/customers${pth}`;
        const init = {
          method: id === undefined ? 'POST' : 'PUT',
          accept: "application/json",
          headers: {
            "Content-type": "application/json",
            "Authorization": "Bearer " + GetToken()
          },
          mode: "cors",
          cache: "no-cache",
          body: JSON.stringify({
            "customerId": id === undefined ? 0 : id,
            "name": GetDefaultData(dataItem.name, "name"),
            "accountNumber": GetDefaultData(dataItem.accountNumber, "accountNumber"),
            "description": GetDefaultData(dataItem.description, "description"),
            "paymentMethod": GetDefaultData(dataItem.paymentMethod, "paymentMethod"),
            "address1": GetDefaultData(dataItem.address1, "address1"),
            "address2": GetDefaultData(dataItem.address2, "address2"),
            "suburb": GetDefaultData(dataItem.suburb, "suburb"),
            "state": GetDefaultData(dataItem.state, "state"),
            "postcode": GetDefaultData(dataItem.postcode, "postcode"),
            "country": GetDefaultData(dataItem.country, "country"),
            "phone": GetDefaultData(dataItem.phone, "phone"),
            "fax": GetDefaultData(dataItem.fax, "fax"),
            "mobile": GetDefaultData(dataItem.mobile, "mobile"),
            "contact": GetDefaultData(dataItem.contact, "contact"),
            "comment": GetDefaultData(dataItem.comment, "comment"),
            "email": GetDefaultData(dataItem.email, "email"),
            "abn": GetDefaultData(dataItem.abn, "abn"),
            "delivery": GetDefaultData(dataItem.delivery, "delivery"),
            "deliveryAddress1": GetDefaultData(dataItem.deliveryAddress1, "deliveryAddress1"),
            "deliveryAddress2": GetDefaultData(dataItem.deliveryAddress2, "deliveryAddress2"),
            "deliverySuburb": GetDefaultData(dataItem.deliverySuburb, "deliverySuburb"),
            "deliveryState": GetDefaultData(dataItem.deliveryState, "deliveryState"),
            "deliveryPostcode": GetDefaultData(dataItem.deliveryPostcode, "deliveryPostcode"),
            "deliveryZone": GetDefaultData(dataItem.deliveryZone, "deliveryZone"),
            "deliveryPhone": GetDefaultData(dataItem.deliveryPhone, "deliveryPhone"),
            "tagId": GetDefaultData(dataItem.tagId, "tagId"),
            "customerType": type,
            "fixedCharge": GetDefaultData(dataItem.fixedCharge, "fixedCharge"),
            "discount": GetDefaultData(dataItem.discount, "discount")*100,
            "noGST": GetDefaultData(dataItem.noGST, "nOGST"),
            "hidePriceOnTicket": GetDefaultData(dataItem.hidePriceOnTicket, "hidePriceOnTicket"),
            "ticketCopies": GetDefaultData(dataItem.ticketCopies, "ticketCopies"),
            "fixedCartage": GetDefaultData(dataItem.fixedCartage, "fixedCartage"),
            "isOrderNoRequired": GetDefaultData(dataItem.isOrderNoRequired, "isOrderNoRequired"),
            "isAlwaysReqOrderNoPerTxn": GetDefaultData(dataItem.isAlwaysReqOrderNoPerTxn, "isAlwaysReqOrderNoPerTxn"),
            "docketInitials": GetDefaultData(dataItem.docketInitials, "docketInitials"),
            "myobSyncInterval": GetDefaultData(dataItem.myobSyncInterval, "myobSyncInterval"),
            "monthlySyncDay": GetDefaultData(dataItem.monthlySyncDay, "monthlySyncDay"),
            "siteList": siteList,
            "truckList": truckList,
            "isActive": GetDefaultData(dataItem.active, "active"),
            "sMSDocket": GetDefaultData(dataItem.smsDocket, "smsDocket"),
            "emailDocket": GetDefaultData(dataItem.emailDocket, "emailDocket"),
           })
        }
        fetch(url, init).then(response => {
            if (response.ok) {
                var msg = "";
                if (type === 'C') {
                    msg = id === undefined ? "Customer created" : "Customer updated";
                }
                else {
                    msg = id === undefined ? "Supplier created" : "Supplier updated";
                }
                Redirect(msg);
                return;
            } else {
                return response.json();
            }
        }).then(d => {
            if (d !== undefined) alert("Enable to create customer: " + d.detail);
        });
    }

    const onMyobChanged = (ev) => {
        let v = { ...values };
        v.myobSyncInterval = ev.value;
        setValues(v);
        setIsMonthly(ev.value === "Monthly");
    }

    // navigation
    const GoBack = () => Redirect("");
    const Redirect = (msg = "") => {
        if(type === 'C'){
            navigate("/customers", {state: {msg: msg}});
        }
        else{
            navigate("/suppliers", {state: {msg: msg}});
        }
       
    }

    const AddressChanged = (e) => {
        if (e !== null && e.value !== undefined && e.value.terms !== undefined && e.value.terms.length > 2) {
            let v = { ...values };
            v.address1 = e.value.structured_formatting.main_text;
            v.address2 = "";
            var ind = 1;
            if (e.value.terms.length > 3) ind = e.value.terms.length - 2;
            v.suburb = e.value.terms[ind++].value;
            v.state = e.value.terms[ind].value;
            setValues(v);
            setFormkey(new Date());
        }
    }

    const DeliveryAddressChanged = (e) => {
        if (e !== null && e.value !== undefined && e.value.terms !== undefined && e.value.terms.length > 2) {
            let v = { ...values };
            v.deliveryAddress1 = e.value.structured_formatting.main_text;
            v.deliveryAddress2 = "";
            var ind = 1;
            if (e.value.terms.length > 3) ind = e.value.terms.length - 2;
            v.deliverySuburb = e.value.terms[ind++].value;
            v.deliveryState = e.value.terms[ind].value;
            setValues(v);
            setFormkey(new Date());
        }
    }

    const DisplayAddressMap = (e) => {
        let addr = values.address1;
        if (!values.address2 !== "") addr += "+" + values.address2;
        if (!values.suburb !== "") addr += "+" + values.suburb;
        if (!values.state !== "") addr += "+" + values.state;
        if (!values.postcode !== "") addr += "+" + values.postcode;
        addr += "+AU";
        DisplayMap(addr);
    }

    const DisplayDeliveryMap = (e) => {
        let addr = values.deliveryAddress1;
        if (!values.deliveryAddress2 !== "") addr += "+" + values.deliveryAddress2;
        if (!values.deliverySuburb !== "") addr += "+" + values.deliverySuburb;
        if (!values.deliveryState !== "") addr += "+" + values.deliveryState;
        if (!values.deliveryPostcode !== "") addr += "+" + values.deliveryPostcode;
        addr += "+AU";
        DisplayMap(addr);
    }

    const DisplayMap = (addr) => {
        let a = encodeURIComponent(addr);
        var url = `${configData.SERVER_URL}apiv7/private/getlocation?address=${a}`;
        const init = {
            method: 'GET',
            accept: "application/json",
            headers: {
                "Content-type": "application/json",
                "Authorization": "Bearer " + GetToken()
            },
            mode: "cors",
            cache: "no-cache"
        }
        fetch(url, init).then(response => response.json()).then(l => {
            let m = [];
            setCentre([l.latitude, l.longitude]);
            m.push({ position: [l.latitude, l.longitude], name: values.address1 });
            setMarkers(m);
            setShowMap(true);
            setFormkey(formkey + 1);
        });
    }


    if (permissions && !permissions.CanView) navigate("error401");
    if (permissions && !permissions.CanEdit && id !== undefined) Redirect("Error: You do not have permission to edit customers");
    if (permissions && !permissions.CanCreate && id === undefined) Redirect("Error: You do not have permission to create customers");

    const NameChanged = (e) => { let v = { ...values }; v.name = e.value; setValues(v) };
    const AccountNumberChanged = (e) => { let v = { ...values }; v.accountNumber = e.value; setValues(v) };
    const AbnChanged = (e) => { let v = { ...values }; v.abn = e.value; setValues(v) };
    const PaymentMethodChanged = (e) => { let v = { ...values }; v.paymentMethod = e.value; setValues(v) };
    const FixedChargeChanged = (e) => { let v = { ...values }; v.fixedCharge = e.value; setValues(v) };
    const DiscountChanged = (e) => { let v = { ...values }; v.discount = e.value; setValues(v) };
    const FixedCartageChanged = (e) => { let v = { ...values }; v.fixedCartage = e.value; setValues(v) };
    const TicketCopiesChanged = (e) => { let v = { ...values }; v.ticketCopies = e.value; setValues(v) };
    const DescriptionChanged = (e) => { let v = { ...values }; v.comment = e.value; setValues(v) };
    const TagIdChanged = (e) => { let v = { ...values }; v.tagId = e.value; setValues(v) };
    const SmsDocketChanged = (e) => { let v = { ...values }; v.smsDocket = e.value; setValues(v) };
    const EmailDocketChanged = (e) => { let v = { ...values }; v.emailDocket = e.value; setValues(v) };
    const HidePriceOnTicketChanged = (e) => { let v = { ...values }; v.hidePriceOnTicket = e.value; setValues(v) };
    const NoGstChanged = (e) => { let v = { ...values }; v.noGST = e.value; setValues(v) };
    const IsOrderNoRequiredChanged = (e) => { let v = { ...values }; v.isOrderNoRequired = e.value; setValues(v) };
    const IsAlwaysReqOrderNoChanged = (e) => { let v = { ...values }; v.isAlwaysReqOrderNoPerTxn = e.value; setValues(v) };
    const ActiveChanged = (e) => { let v = { ...values }; v.active = e.value; setValues(v) };

    const Address2Changed = (e) => { let v = { ...values }; v.address2 = e.value; setValues(v) };
    const SuburbChanged = (e) => { let v = { ...values }; v.suburb = e.value; setValues(v) };
    const StateChanged = (e) => { let v = { ...values }; v.state = e.value; setValues(v) };
    const PostcodeChanged = (e) => { let v = { ...values }; v.postcode = e.value; setValues(v) };
    const PhoneChanged = (e) => { let v = { ...values }; v.phone = e.value; setValues(v) };
    const ContactChanged = (e) => { let v = { ...values }; v.contact = e.value; setValues(v) };
    const MobileChanged = (e) => { let v = { ...values }; v.mobile = e.value; setValues(v) };
    const EmailChanged = (e) => { let v = { ...values }; v.email = e.value; setValues(v) };
    const CountryChanged = (e) => { let v = { ...values }; v.country = e.value; setValues(v) };

    const DeliveryAddress2Changed = (e) => { let v = { ...values }; v.deliveryAddress2 = e.value; setValues(v) };
    const DeliverySuburbChanged = (e) => { let v = { ...values }; v.deliverySuburb = e.value; setValues(v) };
    const DeliveryStateChanged = (e) => { let v = { ...values }; v.deliveryState = e.value; setValues(v) };
    const DeliveryPostcodeChanged = (e) => { let v = { ...values }; v.deliveryPostcode = e.value; setValues(v) };
    const DeliveryZoneChanged = (e) => { let v = { ...values }; v.deliveryZone = e.value; setValues(v) };
    const DeliveryPhoneChanged = (e) => { let v = { ...values }; v.deliveryPhone = e.value; setValues(v) };
    const DeliveryDetailsChanged = (e) => { let v = { ...values }; v.deliveryDetails = e.value; setValues(v) };

    const MonthlySyncDayChanged = (e) => { let v = { ...values }; v.monthlySyncDay = e.value; setValues(v) };
    const SitesChanged = (e) => { let v = { ...values }; v.customerSites = e.value; setValues(v) };
    const TrucksChanged = (e) => { let v = { ...values }; v.customerTrucks = e.value; setValues(v) };

    // form validators
    const textValidator = (value) =>  !value ? "Please enter a value" : "";
    return (
        <div style={{ padding: 20, maxWidth: 1200, margin: "auto" }}>
            <Form initialValues={values} key={formkey} render={(formRenderProps) => (
                <FormElement>
                    <fieldset className="k-form-fieldset" style={{ border: "none", padding: 0 }}>

                        {/* Customer Details Panel */}
                        {GetField("customerDetails")["isVisible"] && (
                            <ExpansionPanel
                                title={GetField("customerDetails")["label"]}
                                expanded={expandedPanels.customerDetails}
                                onToggle={() => togglePanel("customerDetails")}
                            >
                                <div className="row">
                                    {GetField("name")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="name"
                                                name="name"
                                                label={GetField("name")["label"]}
                                                component={FormInput}
                                                maxLength={100}
                                                validator={textValidator}
                                                onChange={NameChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("accountNumber")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="accountNumber"
                                                name="accountNumber"
                                                label={GetField("accountNumber")["label"]}
                                                component={FormInput}
                                                onChange={AccountNumberChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("abn")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="abn"
                                                name="abn"
                                                label={GetField("abn")["label"]}
                                                component={FormInput}
                                                onChange={AbnChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("paymentMethod")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="paymentMethod"
                                                name="paymentMethod"
                                                label={GetField("paymentMethod")["label"]}
                                                component={FormDropDownList}
                                                data={paymentMethods}
                                                onChange={PaymentMethodChanged}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="row">
                                    {GetField("fixedCharge")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="fixedCharge"
                                                name="fixedCharge"
                                                label={GetField("fixedCharge")["label"]}
                                                component={FormNumericTextBox}
                                                min={0}
                                                max={100000}
                                                format="c2"
                                                onChange={FixedChargeChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("discount")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="discount"
                                                name="discount"
                                                label={GetField("discount")["label"]}
                                                component={FormNumericTextBox}
                                                min={0}
                                                max={100}
                                                format="p2"
                                                spinners={false}
                                                onChange={DiscountChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("fixedCartage")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="fixedCartage"
                                                name="fixedCartage"
                                                label={GetField("fixedCartage")["label"]}
                                                component={FormNumericTextBox}
                                                min={0}
                                                max={100000}
                                                format="c2"
                                                onChange={FixedCartageChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("ticketCopies")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="ticketCopies"
                                                name="ticketCopies"
                                                label={GetField("ticketCopies")["label"]}
                                                component={FormNumericTextBox}
                                                min={0}
                                                max={10}
                                                onChange={TicketCopiesChanged}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="row">
                                    {GetField("comment")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="comment"
                                                name="comment"
                                                label={GetField("comment")["label"]}
                                                component={FormTextArea}
                                                onChange={DescriptionChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("tagId")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="tagId"
                                                name="tagId"
                                                label={GetField("tagId")["label"]}
                                                component={FormInput}
                                                onChange={TagIdChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("smsDocket")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="smsDocket"
                                                name="smsDocket"
                                                label={GetField("smsDocket")["label"]}
                                                component={FormSwitch}
                                                onChange={SmsDocketChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("emailDocket")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="emailDocket"
                                                name="emailDocket"
                                                label={GetField("emailDocket")["label"]}
                                                component={FormSwitch}
                                                onChange={EmailDocketChanged}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="row">
                                    {GetField("hidePriceOnTicket")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="hidePriceOnTicket"
                                                name="hidePriceOnTicket"
                                                label={GetField("hidePriceOnTicket")["label"]}
                                                component={FormSwitch}
                                                onChange={HidePriceOnTicketChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("noGST")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="noGST"
                                                name="noGST"
                                                label={GetField("noGST")["label"]}
                                                component={FormSwitch}
                                                onChange={NoGstChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("isOrderNoRequired")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="isOrderNoRequired"
                                                name="isOrderNoRequired"
                                                label={GetField("isOrderNoRequired")["label"]}
                                                component={FormSwitch}
                                                onChange={IsOrderNoRequiredChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("isAlwaysReqOrderNoPerTxn")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="isAlwaysReqOrderNoPerTxn"
                                                name="isAlwaysReqOrderNoPerTxn"
                                                label={GetField("isAlwaysReqOrderNoPerTxn")["label"]}
                                                component={FormSwitch}
                                                onChange={IsAlwaysReqOrderNoChanged}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="row">
                                    {GetField("active")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="active"
                                                name="active"
                                                label={GetField("active")["label"]}
                                                component={FormSwitch}
                                                onChange={ActiveChanged}
                                            />
                                        </div>
                                    )}
                                </div>
                            </ExpansionPanel>
                        )}

                        {/* Address Details Panel */}
                        {GetField("addressDetails")["isVisible"] && (
                            <ExpansionPanel
                                title={GetField("addressDetails")["label"]}
                                expanded={expandedPanels.addressDetails}
                                onToggle={() => togglePanel("addressDetails")}
                            >
                                <div className="row">
                                    {GetField("address1")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="address1"
                                                name="address1"
                                                label={GetField("address1")["label"]}
                                                component={FormAddress}
                                                onChange={AddressChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("address2")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="address2"
                                                name="address2"
                                                label={GetField("address2")["label"]}
                                                component={FormInput}
                                                onChange={Address2Changed}
                                            />
                                        </div>
                                    )}
                                    {GetField("suburb")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="suburb"
                                                name="suburb"
                                                label={GetField("suburb")["label"]}
                                                component={FormInput}
                                                onChange={SuburbChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("state")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="state"
                                                name="state"
                                                label={GetField("state")["label"]}
                                                component={FormDropDownList}
                                                data={states}
                                                onChange={StateChanged}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="row">
                                    {GetField("postcode")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="postcode"
                                                name="postcode"
                                                label={GetField("postcode")["label"]}
                                                component={FormInput}
                                                onChange={PostcodeChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("phone")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="phone"
                                                name="phone"
                                                label={GetField("phone")["label"]}
                                                component={FormInput}
                                                onChange={PhoneChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("contact")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="contact"
                                                name="contact"
                                                label={GetField("contact")["label"]}
                                                component={FormInput}
                                                onChange={ContactChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("mobile")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="mobile"
                                                name="mobile"
                                                label={GetField("mobile")["label"]}
                                                component={FormInput}
                                                onChange={MobileChanged}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="row">
                                    {GetField("email")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="email"
                                                name="email"
                                                label={GetField("email")["label"]}
                                                component={FormInput}
                                                onChange={EmailChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("country")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="country"
                                                name="country"
                                                label={GetField("country")["label"]}
                                                component={FormInput}
                                                onChange={CountryChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("fax")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="fax"
                                                name="fax"
                                                label={GetField("fax")["label"]}
                                                component={FormInput}
                                            />
                                        </div>
                                    )}
                                    {GetField("showMap")["isVisible"] && (
                                        <div className="col-md-3" style={{ paddingTop: 30 }}>
                                            <Button onClick={DisplayAddressMap}>
                                                {GetField("showMap", "Show Map")["label"]}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </ExpansionPanel>
                        )}

                        {/* Delivery Details Panel */}
                        {GetField("deliveryDetails")["isVisible"] && (
                            <ExpansionPanel
                                title={GetField("deliveryDetails")["label"]}
                                expanded={expandedPanels.deliveryDetails}
                                onToggle={() => togglePanel("deliveryDetails")}
                            >
                                <div className="row">
                                    {GetField("deliveryAddress1")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="deliveryAddress1"
                                                name="deliveryAddress1"
                                                label={GetField("deliveryAddress1")["label"]}
                                                component={FormAddress}
                                                onChange={DeliveryAddressChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("deliveryAddress2")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="deliveryAddress2"
                                                name="deliveryAddress2"
                                                label={GetField("deliveryAddress2")["label"]}
                                                component={FormInput}
                                                onChange={DeliveryAddress2Changed}
                                            />
                                        </div>
                                    )}
                                    {GetField("deliverySuburb")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="deliverySuburb"
                                                name="deliverySuburb"
                                                label={GetField("deliverySuburb")["label"]}
                                                component={FormInput}
                                                onChange={DeliverySuburbChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("deliveryState")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="deliveryState"
                                                name="deliveryState"
                                                label={GetField("deliveryState")["label"]}
                                                component={FormDropDownList}
                                                data={states}
                                                onChange={DeliveryStateChanged}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="row">
                                    {GetField("deliveryPostcode")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="deliveryPostcode"
                                                name="deliveryPostcode"
                                                label={GetField("deliveryPostcode")["label"]}
                                                component={FormInput}
                                                onChange={DeliveryPostcodeChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("deliveryZone")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="deliveryZone"
                                                name="deliveryZone"
                                                label={GetField("deliveryZone")["label"]}
                                                component={FormInput}
                                                onChange={DeliveryZoneChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("deliveryPhone")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="deliveryPhone"
                                                name="deliveryPhone"
                                                label={GetField("deliveryPhone")["label"]}
                                                component={FormInput}
                                                onChange={DeliveryPhoneChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("deliveryContact")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="deliveryContact"
                                                name="deliveryContact"
                                                label={GetField("deliveryContact")["label"]}
                                                component={FormInput}
                                                onChange={DeliveryDetailsChanged}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="row">
                                    {GetField("myobSyncInterval")["isVisible"] && (
                                        <div className="col-md-3">
                                            <Field
                                                id="myobSyncInterval"
                                                name="myobSyncInterval"
                                                label={GetField("myobSyncInterval")["label"]}
                                                component={FormDropDownList}
                                                data={syncIntervals}
                                                onChange={onMyobChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("monthlySyncDay")["isVisible"] && isMonthly && (
                                        <div className="col-md-3">
                                            <Field
                                                id="monthlySyncDay"
                                                name="monthlySyncDay"
                                                label={GetField("monthlySyncDay")["label"]}
                                                component={FormNumericTextBox}
                                                min={1}
                                                max={31}
                                                onChange={MonthlySyncDayChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("showMap")["isVisible"] && (
                                        <div className="col-md-3" style={{ paddingTop: 30 }}>
                                            <Button onClick={DisplayDeliveryMap}>
                                                {GetField("showMap", "Show Map")["label"]}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </ExpansionPanel>
                        )}

                        {/* Site Details Panel */}
                        {GetField("siteDetails")["isVisible"] && (
                            <ExpansionPanel
                                title={GetField("siteDetails")["label"]}
                                expanded={expandedPanels.siteDetails}
                                onToggle={() => togglePanel("siteDetails")}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: "1rem",
                                        padding: 10,
                                        justifyContent: "space-between",
                                    }}
                                >
                                    {GetSession().SiteID === 1 && GetField("customerSites")["isVisible"] && (
                                        <div style={{ flex: "1 1 500px", minWidth: 500 }}>
                                            <label>{GetField("customerSites")["label"]}</label>
                                            <Field
                                                component={MultiSelectTreeEditor}
                                                name="customerSites"
                                                data={sitesTreeData}
                                                dataItemKey={dataItemKey}
                                                checkField={checkField}
                                                size="large"
                                                onChange={SitesChanged}
                                            />
                                        </div>
                                    )}
                                    {GetField("customerTrucks")["isVisible"] && (
                                        <div style={{ flex: "1 1 500px", minWidth: 500 }}>
                                            <label>{GetField("customerTrucks")["label"]}</label>
                                            <Field
                                                component={MultiSelectTreeEditor}
                                                name="customerTrucks"
                                                data={trucksTreeData}
                                                dataItemKey={dataItemKey}
                                                checkField={checkField}
                                                size="large"
                                                onChange={TrucksChanged}
                                            />
                                        </div>
                                    )}
                                </div>
                            </ExpansionPanel>
                        )}

                    </fieldset>

                    {/* Buttons */}
                    <div
                        className="k-form-buttons"
                        style={{ marginTop: 20, display: "flex", gap: "1rem" }}
                    >
                        <Button
                            themeColor="primary"
                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                            onClick={handleSubmit} disabled={submitDisabled}
                        >
                            {saveName}
                        </Button>
                        <Button
                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                            onClick={GoBack}
                        >
                            Back to List
                        </Button>
                    </div>
                </FormElement>
            )} />

            {/* Map Dialog */}
            {showMap && (
                <Dialog title="Address Map" onClose={() => setShowMap(false)}>
                    <div style={{ width: 1000 }}>
                        <Map center={centre} zoom={zoom} minZoom={5}>
                            <MapLayers>
                                <MapTileLayer urlTemplate={tileUrl} />
                                <MapMarkerLayer data={markers} locationField="position" titleField="name" />
                            </MapLayers>
                        </Map>
                    </div>
                </Dialog>
            )}
        </div>
    );
}


export default EditCustomers
